import classNames from 'classnames';
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import styles from './SingleCalendar.module.scss';
import './react-datepicker.css';
import { useState } from 'react';
import { DATE_FORMAT } from 'constants/common.constants';

type DatePickerProps = {
    date: Date | null;
    minDate?: Date | null;
    maxDate?: Date | null;
    calendarDays?: Array<Date>;
    width?: number;
    disabled?: boolean;
    resetDate?: boolean;
    onChangeDate?: (date: Date | null) => void;
    stretch?: boolean;
};

const SingleCalendar = ({
    date,
    disabled,
    minDate,
    maxDate,
    onChangeDate,
    stretch = false,
    resetDate = false,
}: DatePickerProps) => {
    const [inputValue, setInputValue] = useState(date ? dayjs(date).format(DATE_FORMAT) : '');
    const today = dayjs().startOf('day').toDate();
    const [isOpen, setIsOpen] = useState(false);

    const getDayClassName = (date: Date) => {
        const classes: string[] = [];
        const dow = date.getDay();
        if (dow === 6) classes.push('saturday');
        if (dow === 0) classes.push('sunday');

        const target = dayjs(date).startOf('day');
        if (target.isAfter(today, 'day')) classes.push('day--future');
        if (minDate && target.isBefore(dayjs(minDate).startOf('day'))) classes.push('day--before-min');
        if (maxDate && target.isAfter(dayjs(maxDate).startOf('day'))) classes.push('day--after-max');

        return classes.join(' ');
    };

    const formatWeekDay = (name: string) => {
        return name.toUpperCase().substr(0, 3);
    };

    const onUpdateDate = (date: Date | null) => {
        if (date) {
            const d = dayjs(date);
            if (!inRange(d)) return; // 범위 밖이면 무시
            const formatted = d.format(DATE_FORMAT);
            setInputValue(formatted);
            onChangeDate?.(date);
        }
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;

        if (/^\d{8}$/.test(value)) {
            value = `${value.substring(0, 4)}-${value.substring(4, 6)}-${value.substring(6, 8)}`;
        }
        setInputValue(value);

        // 글자수가 10자리여야만 파싱 시도
        if (value.length === 10) {
            const parsed = dayjs(value, DATE_FORMAT, true);
            if (parsed.isValid() && inRange(parsed)) {
                onChangeDate?.(parsed.toDate());
                setIsOpen(false);
            }
        }
    };

    const handleReset = () => {
        onChangeDate?.call(null, null);
    };

    // ★ 범위 체크 유틸
    const inRange = (d: dayjs.Dayjs) => {
        const day = d.startOf('day');
        if (minDate && day.isBefore(dayjs(minDate).startOf('day'))) return false;
        if (maxDate && day.isAfter(dayjs(maxDate).startOf('day'))) return false;
        // 기존 정책 유지: 오늘 이후(미래) 선택 금지
        if (day.isAfter(dayjs(today))) return false;
        return true;
    };

    return (
        <label
            className={classNames([
                `${disabled ? 'calendar-disabled' : ''}`,
                styles.container,
                stretch && styles['-stretch'],
            ])}
        >
            <i
                className={classNames([
                    styles.ico,
                    styles['-calendar'],
                    {
                        [styles['selected']]: !disabled && date,
                        [styles['disabled']]: disabled,
                    },
                ])}
            ></i>
            <DatePicker
                // ▼ 제어 모드
                open={isOpen}
                onCalendarOpen={() => setIsOpen(true)}
                onCalendarClose={() => setIsOpen(false)}
                onClickOutside={() => setIsOpen(false)}
                onInputClick={() => !disabled && setIsOpen(true)}
                minDate={minDate ?? undefined}
                maxDate={maxDate ?? undefined}
                filterDate={d => inRange(dayjs(d))}
                selected={date}
                onChange={picked => {
                    onUpdateDate(picked); // 기존 로직 유지
                    setIsOpen(false); // ← 날짜 선택 시 확실히 닫기
                }}
                // 제어 모드에선 shouldCloseOnSelect 의미가 희미 → 꺼두는 걸 권장
                shouldCloseOnSelect={false}
                popperPlacement="top"
                wrapperClassName={date ? 'selected' : ''}
                disabled={disabled}
                placeholderText="YYYY-MM-DD"
                renderCustomHeader={({
                    date,
                    decreaseMonth,
                    increaseMonth,
                    decreaseYear,
                    increaseYear,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                }) => (
                    <div className="custom_header">
                        <div className={styles['button_area']}>
                            <button
                                type="button"
                                className={classNames([styles['header-btn'], styles['-double-left']])}
                                onClick={decreaseYear}
                                disabled={prevMonthButtonDisabled}
                            />
                            <button
                                type="button"
                                className={classNames([styles['header-btn'], styles['-left']])}
                                onClick={decreaseMonth}
                                disabled={prevMonthButtonDisabled}
                            />
                        </div>
                        <div className="header-date">{dayjs(date).format('YYYY.MM')}</div>
                        <div className={styles['button_area']}>
                            <button
                                type="button"
                                className={classNames([styles['header-btn'], styles['-right']])}
                                onClick={increaseMonth}
                                disabled={nextMonthButtonDisabled}
                            />
                            <button
                                type="button"
                                className={classNames([styles['header-btn'], styles['-double-right']])}
                                onClick={increaseYear}
                                disabled={prevMonthButtonDisabled}
                            />
                        </div>
                    </div>
                )}
                onChangeRaw={e => {
                    e?.preventDefault();
                }} // ✅ 여기서 직접 입력 완전히 막기
                customInput={
                    <input
                        readOnly
                        value={inputValue}
                        onChange={e => e.preventDefault()} // ✅ 타이핑 막음
                        disabled={disabled}
                        placeholder={DATE_FORMAT}
                    />
                }
                dateFormat="yyyy-MM-dd" // ← react-datepicker는 date-fns 토큰 사용
                isClearable={false}
                dayClassName={d => getDayClassName(d)}
                formatWeekDay={formatWeekDay}
                renderDayContents={day => <span className="day__text">{day}</span>}
            />
            {resetDate && !disabled && date && (
                <button className={styles['reset-btn']} onClick={handleReset} title="Reset Date">
                    <i className={styles['ico']}></i>
                </button>
            )}
        </label>
    );
};

export default SingleCalendar;
